import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  HANDLE_BORDER_WIDTH,
  SELECTION_COLOR,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import { chromeGeometry } from '@/lib/shapes/selection-chrome-preview'
import { selectionChromeFrameOfShape } from '@/lib/shapes/shape-coordinates'

/**
 * Alt/ctrl-hover highlight: the box drawn around the shape that a selection-cycling hover is
 * currently targeting.
 *
 * Migrated out of `ShapeManager`, where it was a `border: 1px` plus a `boxShadow` blur on a div
 * inside the zoom-scaled canvas — so both the outline weight *and* the glow radius were document
 * pixels and grew with zoom. Here both are true screen pixels.
 *
 * This is the *box* indicator. `ShapeRenderer` separately draws an outline that traces the shape's
 * own path on alt-hover; that one already used `vector-effect="non-scaling-stroke"` and is
 * unaffected.
 *
 * Deliberately independent of selection chrome: hover targeting stays useful while another surface
 * owns the selection (node edit, the text tool, a mixed shape+text selection), so this renders even
 * when chrome is suppressed.
 */

/** Screen px. Matches the old `boxShadow: 0 0 6px` blur, now unaffected by zoom. */
const HOVER_GLOW_RADIUS_PX = 6
const HOVER_GLOW_COLOR = '#0284C7'

export type HoverOutlineLayerProps = {
  /** The alt-hovered shape, or null when nothing is being hovered for cycling. */
  shape: ShapeData | null
  /** Current canvas zoom. */
  scale: number
}

export function HoverOutlineLayer({ shape, scale }: HoverOutlineLayerProps) {
  if (!shape) return null

  const { corners } = chromeGeometry(selectionChromeFrameOfShape(shape), { scale })

  return (
    <polygon
      data-hover-outline=""
      points={corners.map((p) => `${p.x},${p.y}`).join(' ')}
      fill="none"
      stroke={SELECTION_COLOR}
      strokeWidth={HANDLE_BORDER_WIDTH}
      style={{
        pointerEvents: 'none',
        filter: `drop-shadow(0 0 ${HOVER_GLOW_RADIUS_PX}px ${HOVER_GLOW_COLOR})`,
      }}
    />
  )
}

export default HoverOutlineLayer
